import {
  Anchor,
  Button,
  Group,
  HoverCard,
  Stack,
  StepperStep,
  Text,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import { getProps } from "../notes/ItemProps";
import { parseDate } from "../../../../../../../functions/dates";
import { useLeadsSheetStore } from "../../../../../../../stores/LeadsStore";
import { IconExternalLink, IconEye } from "@tabler/icons-react";
import { useLanguage } from "../../../../../../../stores/LanguageStore";
import { useEffect, useMemo, useState } from "react";
import EmailModalPreviewPrm from "../../../../../../../components/View/EmailModalPreviewPrm";
import { getCallColor } from "../../../../utils";
import { findList } from "../../../../../../../api/ContactAPI";

const SummaryItem = ({ item, isLast }) => {
  const lang = useLanguage((s) => s.language);
  const Icon = getProps(item).icon;
  const current = useLeadsSheetStore((s) => s.current);

  const programmation = useMemo(() => {
    return current?.programmations?.find(
      (x) => x.programmation_id === item.workflow_programmation_id
    );
  }, [current?.programmations, item.workflow_programmation_id]);

  const [listName, setListName] = useState("");
  useEffect(() => {
    if (item?.contact_list_id) {
      findList(item.contact_list_id).then((r) => {
        setListName(r.data.contact_list_profile?.name || "");
      });
    }
  }, [item.contact_list_id])  

  return (
    <Stack gap={4} mt={-4} w={"100%"}>
      <Group>
        <StepperStep
          component={Group}
          // align="start"
          miw={120}
          gap={0}
          pl={3}
          style={{ cursor: "text" }}
          icon={
            <ThemeIcon
              variant="white"
              radius={"lg"}
              color={getProps(item)?.iconColor}
              style={{ boxShadow: "#00000021 1px 1px 3px" }}
              mt={-4}
            >
              <Icon size={16} />
            </ThemeIcon>
          }
          label={
            <Group
              gap={"xs"}
              bg={"#fcfbff"}
              px={"xs"}
              pt={4}
              style={{
                borderTopLeftRadius: 4,
                borderTopRightRadius: 4,
                border: "1px solid #00000018",
                borderBottom: 0,
              }}
              miw={120}
            >
              {item?.history_type === "call" ? (
                <Text>{item?.name || getProps(item).label}</Text>
              ) : (
                <Text>
                  {getProps(item)?.label?.replace(
                    "-",
                    item?.workflow_programmation_id ? "-" : ""
                  )}{" "}

                  {isLast && item?.contact_list_id ? (
                    <>
                      {lang.leads.list_from}{" "}
                      <Anchor
                        href={`/contact-lists/${item.contact_list_id}`}
                        target="_blank"
                      >
                        {listName} #{item.contact_list_id}
                      </Anchor>
                    </>
                  ) : (
                    ""
                  )}
                </Text>
              )}

              {programmation?.workflow_name && (
                <Anchor
                  fw={500}
                  href={`/statistics/campaigns/running?highlight=${programmation?.programmation_id}`}
                  target="_blank"
                >
                  {programmation?.workflow_name}
                  {item?.workflow_step_id && (
                    <span style={{ fontSize: 12, marginLeft: 16 }}>
                      ( {lang.prm.resume.step} #{item.workflow_step_id} )
                    </span>
                  )}
                </Anchor>
              )}
            </Group>
          }
          description={
            <Group
              bg={"#fcfbff"}
              px={"xs"}
              pb={6}
              mt={-4}
              pt={4}
              miw={120}
              style={{
                borderBottomLeftRadius: 4,
                borderBottomRightRadius: 4,
                border: "1px solid #00000018",
                borderTop: 0,
              }}
            >
              <Tooltip
                label={new Date(item?.date)?.toLocaleString()}
                withArrow
                openDelay={500}
              >
                <Text
                  truncate="end"
                  size="sm"
                  c={
                    item?.history_type === "call"
                      ? getCallColor(item?.call_date)
                      : undefined
                  }
                >
                  {parseDate(item?.date, true)}
                </Text>
              </Tooltip>
            </Group>
          }
          mih={0}
        />

        {item.history_type === "sent" || item.history_type === "reply" ? (
          item.type === "linkedin" || item.type === "sms" ? (
            <HoverCard zIndex={400} position="top" withArrow width={300}>
              <HoverCard.Target>
                <Button
                  variant="light"
                  leftSection={<IconEye size={18} />}
                  radius={"md"}
                  style={{ cursor: "default" }}
                >
                  {lang.components.email_preview.button_label}
                </Button>
              </HoverCard.Target>

              <HoverCard.Dropdown>
                {item.message || item.reply}
              </HoverCard.Dropdown>
            </HoverCard>
          ) : (
            ""
          )
        ) : (
          ""
        )}

        {item.event === "email_sent" && (
          <EmailModalPreviewPrm
            step_id={item.workflow_step_id}
            prog_id={item.workflow_programmation_id}
          />
        )}

        {item.history_type === "reply" && item.type === "email" && (
          <EmailModalPreviewPrm
            html={item.reply}
            step_id={item.workflow_step_id}
            prog_id={item.workflow_programmation_id}
          />
        )}

        {item.history_type === "sent" &&
          item.type === "email" &&
          item?.message && <EmailModalPreviewPrm html={item.message} />}

        {item?.event === "link_click" && (
          <Tooltip label={lang.prm.details.history.view_link}>
            <Anchor target="_blank" href={item?.additional_data}>
              <Button
                variant="light"
                className="on-hover-opacity"
                color="primary"
                leftSection={<IconExternalLink size={18} />}
                size="xs"
              >
                {item?.additional_data}
              </Button>
            </Anchor>
          </Tooltip>
        )}

        {item?.history_type === "note" && (
          <HoverCard zIndex={400} position="right" withArrow width={300}>
            <HoverCard.Target>
              <Button
                variant="light"
                leftSection={<IconEye size={18} />}
                radius={"md"}
                style={{ cursor: "default" }}
              >
                {lang.global.view}
              </Button>
            </HoverCard.Target>

            <HoverCard.Dropdown>
              <iframe
                onLoad={(e) => {
                  let contentHeight =
                    e.target.contentWindow.document.body.scrollHeight + 16;
                  e.target.style.height = contentHeight + "px";
                }}
                title={`note-${item?.id}`}
                srcDoc={`
        <head>
          <style>
            p { margin: 0 }
          </style>
        </head>
        <div style="font-family: sans-serif; font-size: 13px;">${item.note}</div>
        `}
                style={{
                  width: "100%",
                  border: "none",
                  height: "100%",
                  minHeight: 200,
                }}
              // sandbox=""
              />
            </HoverCard.Dropdown>
          </HoverCard>
        )}
      </Group>
      {!isLast && (
        <Group
          mih={32}
          pl={26}
          style={{
            borderLeft: isLast
              ? "1px solid transparent"
              : "1px solid #00000021",
          }}
          ml={17}
          mt={-10}
        ></Group>
      )}
    </Stack>
  );
};

export default SummaryItem;
